import { Link } from "@tanstack/react-router";
import { blogPosts } from "@/lib/blog-data";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";

interface RelatedBlogPostsProps {
  /** Filter by category e.g. "MOT", "Servicing", "Repairs" */
  category?: string;
  /** Max posts to show */
  limit?: number;
  /** Slugs to exclude */
  exclude?: string[];
}

export function RelatedBlogPosts({ category, limit = 3, exclude = [] }: RelatedBlogPostsProps) {
  let posts = blogPosts.filter((p) => !exclude.includes(p.slug));
  if (category) {
    const catPosts = posts.filter((p) => p.category === category);
    posts = catPosts.length >= limit ? catPosts : posts;
  }
  const display = posts.slice(0, limit);

  if (display.length === 0) return null;

  return (
    <AnimatedSection>
      <section className="py-14 md:py-18">
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center">Helpful Guides & Advice</h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">From our blog — practical tips to help you look after your vehicle.</p>
          <StaggerContainer className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {display.map((post) => (
              <StaggerItem key={post.slug}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: post.slug }}
                  className="group flex flex-col rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all h-full"
                >
                  <span className="text-xs font-medium text-primary bg-primary/5 px-2 py-0.5 rounded w-fit">
                    {post.category}
                  </span>
                  <h3 className="mt-2 text-sm font-bold text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">
                    {post.description}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.readTime}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
          <div className="mt-6 text-center">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm hover:text-primary/80 transition-colors">
              Read all articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </AnimatedSection>
  );
}
